import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2,
  maximumFractionDigits: 2 })}`;

const Shell = ({ title, note, tid, children, footer, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
       onClick={onClose}>
    <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
         onClick={(e) => e.stopPropagation()} data-testid={tid}>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      {note && <p className="mt-1 text-xs text-slate-500">{note}</p>}
      {children}
      <div className="mt-4 flex justify-end gap-2">{footer}</div>
    </div>
  </div>
);

/** The vendor's copy: one consolidated order from ABS Warehouse, never a job breakout. */
export function GroupSendDialog({ group, placeOrder, onClose, onDone }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    opsApi.groupRfqPreview(group.id)
      .then((p) => setDraft({ ...p, to: (p.to || []).join(", ") }))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Could not prepare that email");
        onClose();
      });
  }, [group.id, onClose]);

  const send = async () => {
    setBusy(true);
    try {
      await opsApi.groupSend(group.id, {
        to: draft.to.split(",").map((s) => s.trim()).filter(Boolean),
        subject: draft.subject, body: draft.body, place_order: !!placeOrder,
      });
      toast.success(placeOrder ? `${group.group_po_number} placed with the vendor`
        : `Quote requested for ${group.group_po_number}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That email did not send"); }
    finally { setBusy(false); }
  };

  if (!draft) {
    return <Shell tid="group-send-dialog" title="Preparing the email…" onClose={onClose}
                  footer={null}>
      <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Building the consolidated order…
      </div>
    </Shell>;
  }

  return (
    <Shell tid="group-send-dialog" onClose={onClose}
           title={`${placeOrder ? "Place" : "Request a quote for"} · ${group.group_po_number}`}
           note={draft.note}
           footer={<>
             <Button variant="ghost" size="sm" onClick={onClose}
                     data-testid="group-send-cancel">Cancel</Button>
             <Button variant="data" size="sm" onClick={send} disabled={busy} data-testid="group-send-confirm">
               {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
               {placeOrder ? "Send the order" : "Send the request"}
             </Button>
           </>}>
      <label className="mt-3 block text-xs">
        <span className="text-slate-500">To</span>
        <Input value={draft.to} onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
               className="mt-1 h-8 text-xs" data-testid="group-send-to" />
      </label>
      <label className="mt-2 block text-xs">
        <span className="text-slate-500">Subject</span>
        <Input value={draft.subject}
               onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
               className="mt-1 h-8 text-xs" data-testid="group-send-subject" />
      </label>
      <Textarea rows={12} value={draft.body} className="mt-2 font-mono text-xs"
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                data-testid="group-send-body" />
      <p className="mt-2 text-xs text-slate-500">
        {draft.lines.length} part(s) on the vendor&apos;s copy — quantities are combined.
      </p>
    </Shell>
  );
}

/** Quoted unit prices per part. The lump discount, freight and tax are allocated pro-rata by
 *  extended budgeted cost, so every job carries its own share and no other job's. */
export function GroupQuoteDialog({ group, onClose, onDone }) {
  const parts = Object.values(group.lines.reduce((acc, l) => {
    const key = (l.part_number || "").trim().toLowerCase();
    const row = acc[key] || (acc[key] = { part_number: l.part_number, description: l.description,
      qty: 0, budgeted_unit_cost: l.budgeted_unit_cost,
      quoted_unit_cost: l.quoted_unit_cost || "" });
    row.qty += Number(l.qty || 0);
    return acc;
  }, {}));
  const [prices, setPrices] = useState(() => Object.fromEntries(
    parts.map((p) => [p.part_number, p.quoted_unit_cost])));
  const [totals, setTotals] = useState({ lump_discount: group.lump_discount || 0,
    freight_amount: group.freight_amount || 0, tax_amount: group.tax_amount || 0 });
  const [notes, setNotes] = useState(group.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await opsApi.groupQuote(group.id, {
        lines: parts.filter((p) => prices[p.part_number] !== "")
          .map((p) => ({ part_number: p.part_number,
            quoted_unit_cost: Number(prices[p.part_number]) })),
        lump_discount: Number(totals.lump_discount) || 0,
        freight_amount: Number(totals.freight_amount) || 0,
        tax_amount: Number(totals.tax_amount) || 0, notes,
      });
      toast.success(`Quote filed against ${group.group_po_number}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That quote could not be filed"); }
    finally { setBusy(false); }
  };

  return (
    <Shell tid="group-quote-dialog" onClose={onClose}
           title={`Vendor quote · ${group.group_po_number}`}
           note={"Enter the quoted unit price per part. A lump discount, the freight and the tax "
             + "are split pro-rata across every job by extended budgeted cost."}
           footer={<>
             <Button variant="ghost" size="sm" onClick={onClose}
                     data-testid="group-quote-cancel">Cancel</Button>
             <Button size="sm" onClick={save} disabled={busy} data-testid="group-quote-save">
               {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} File the quote
             </Button>
           </>}>
      <table className="mt-3 w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-2 py-1.5 text-left">Part</th>
            <th className="px-2 py-1.5 text-left">Description</th>
            <th className="px-2 py-1.5 text-right">Total qty</th>
            <th className="px-2 py-1.5 text-right">Budgeted unit</th>
            <th className="px-2 py-1.5 text-right">Quoted unit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {parts.map((p) => (
            <tr key={p.part_number}>
              <td className="px-2 py-1 font-mono">{p.part_number || "—"}</td>
              <td className="px-2 py-1">{p.description}</td>
              <td className="px-2 py-1 text-right">{p.qty.toLocaleString()}</td>
              <td className="px-2 py-1 text-right text-slate-500">
                {money(p.budgeted_unit_cost)}
              </td>
              <td className="px-2 py-1 text-right">
                <Input type="number" step="0.01" value={prices[p.part_number]}
                       onChange={(e) => setPrices((s) => ({ ...s,
                         [p.part_number]: e.target.value }))}
                       className="ml-auto h-7 w-24 text-right text-xs"
                       data-testid={`group-quote-price-${p.part_number}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {[["lump_discount", "Lump discount"], ["freight_amount", "Freight"],
          ["tax_amount", "Tax"]].map(([k, label]) => (
          <label key={k} className="block text-xs">
            <span className="text-slate-500">{label}</span>
            <Input type="number" step="0.01" value={totals[k]}
                   onChange={(e) => setTotals((t) => ({ ...t, [k]: e.target.value }))}
                   className="mt-1 h-8 text-xs" data-testid={`group-quote-${k}`} />
          </label>
        ))}
      </div>
      <label className="mt-2 block text-xs">
        <span className="text-slate-500">Notes</span>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)}
               className="mt-1 h-8 text-xs" data-testid="group-quote-notes" />
      </label>
    </Shell>
  );
}

/** Into the ABS warehouse. Each job's FRS rows read `in_warehouse` — picking tickets still move
 *  it to site, so there is only ever one inventory. */
export function GroupReceiveDialog({ group, onClose, onDone }) {
  const open = group.lines.filter((l) => l.outstanding_qty > 0);
  const [qtys, setQtys] = useState(() => Object.fromEntries(
    open.map((l) => [l.id, l.outstanding_qty])));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const lines = open.map((l) => ({ line_id: l.id, qty: Number(qtys[l.id]) || 0 }))
      .filter((l) => l.qty > 0);
    if (!lines.length) { toast.error("Enter a quantity to receive"); return; }
    setBusy(true);
    try {
      await opsApi.groupReceive(group.id, { lines, note });
      toast.success(`Received into the warehouse against ${group.group_po_number}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be received"); }
    finally { setBusy(false); }
  };

  return (
    <Shell tid="group-receive-dialog" onClose={onClose}
           title={`Receive · ${group.group_po_number}`}
           note="It lands in the ABS warehouse and each job's FRS rows follow its own lines."
           footer={<>
             <Button variant="ghost" size="sm" onClick={onClose}
                     data-testid="group-receive-cancel">Cancel</Button>
             <Button size="sm" onClick={save} disabled={busy} data-testid="group-receive-save">
               {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Receive
             </Button>
           </>}>
      {open.length === 0 && (
        <p className="mt-3 text-sm text-slate-600">Everything on this group order is received.</p>
      )}
      <table className="mt-3 w-full text-xs">
        <tbody className="divide-y divide-slate-100">
          {open.map((l) => (
            <tr key={l.id}>
              <td className="px-2 py-1 text-slate-500">{l.job_number}</td>
              <td className="px-2 py-1 font-mono">{l.part_number || "—"}</td>
              <td className="px-2 py-1">{l.description}</td>
              <td className="px-2 py-1 text-right text-slate-500">
                {Number(l.outstanding_qty).toLocaleString()} outstanding
              </td>
              <td className="px-2 py-1 text-right">
                <Input type="number" value={qtys[l.id]}
                       onChange={(e) => setQtys((s) => ({ ...s, [l.id]: e.target.value }))}
                       className="ml-auto h-7 w-20 text-right text-xs"
                       data-testid={`group-receive-qty-${l.id}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="mt-3 block text-xs">
        <span className="text-slate-500">Note</span>
        <Input value={note} onChange={(e) => setNote(e.target.value)}
               className="mt-1 h-8 text-xs" data-testid="group-receive-note" />
      </label>
    </Shell>
  );
}

/** The acknowledgement is an event on the group order. Dates are read deterministically. */
export function GroupAckDialog({ group, onClose, onDone }) {
  const [text, setText] = useState("");
  const [fields, setFields] = useState({ ack_date: "", est_ship_date: "",
    vendor_order_number: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!text.trim() && !fields.ack_date) {
      toast.error("Paste the acknowledgement, or type the dates");
      return;
    }
    setBusy(true);
    try {
      const res = await opsApi.groupAck(group.id, { text, ...fields });
      if (res.duplicate) { toast.info(res.message); onDone(); return; }
      setResult(res.ack);
      toast.success(`Acknowledgement filed against ${group.group_po_number}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be filed"); }
    finally { setBusy(false); }
  };

  return (
    <Shell tid="group-ack-dialog" onClose={onClose}
           title={`Acknowledgement · ${group.group_po_number}`}
           note={"Paste the vendor's confirmation. The acknowledgement date, estimated ship date "
             + "and order number are read from it; the raw text is kept on the record."}
           footer={<>
             <Button variant="ghost" size="sm" onClick={result ? onDone : onClose}
                     data-testid="group-ack-close">{result ? "Done" : "Cancel"}</Button>
             <Button size="sm" onClick={save} disabled={busy} data-testid="group-ack-save">
               {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} File it
             </Button>
           </>}>
      <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)}
                className="mt-3 text-xs" data-testid="group-ack-text"
                placeholder={"Order date: 03/02/2026\nEstimated ship date: 04/15/2026\nSales Order # SO-99812"} />
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {[["ack_date", "Acknowledged", "date"], ["est_ship_date", "Estimated ship", "date"],
          ["vendor_order_number", "Vendor order #", "text"]].map(([k, label, type]) => (
          <label key={k} className="block text-xs">
            <span className="text-slate-500">{label}</span>
            <Input type={type} value={fields[k]}
                   onChange={(e) => setFields((f) => ({ ...f, [k]: e.target.value }))}
                   className="mt-1 h-8 text-xs" data-testid={`group-ack-${k}`} />
          </label>
        ))}
      </div>
      {result && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs"
             data-testid="group-ack-result">
          <p className="font-semibold text-slate-800">
            Read {result.method} · confidence {Number(result.confidence).toFixed(2)}
            {result.needs_review ? " · needs review" : ""}
          </p>
          <p className="mt-0.5 text-slate-600">
            Acknowledged {result.ack_date || "—"} · ships {result.est_ship_date || "—"} ·
            order {result.vendor_order_number || "—"}
          </p>
          {result.missing?.length > 0 && (
            <p className="mt-1 text-amber-700">
              Could not read: {result.missing.join(", ")} — type them above and file again.
            </p>
          )}
        </div>
      )}
    </Shell>
  );
}
